import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ForwardedRef,
} from 'react';
import { forwardRef, useEffect, useState } from 'react';

import classNames from 'classnames';

import styles from './TextArea.module.css';

import { useElementState } from '../../hooks/useElementState';
import type { FieldLabelBase } from '../FieldLabel';
import { FieldLabel } from '../FieldLabel';
import { Text } from '../Text';

/** The props for {@link TextArea}. */
export interface TextAreaProps
  extends FieldLabelBase, ComponentPropsWithoutRef<'textarea'> {
  /** Toggle invalid state */
  invalid?: boolean;
  /** The value for the textarea */
  value?: string;
}

/**
 * Render a multi-line text input control. Implements {@link FieldLabel}.
 *
 * @param props - The {@link TextAreaProps} to render the textarea field.
 * @see https://corgi-x.tfd.engineering/components/textarea
 */
export const TextArea = forwardRef(TextArea_);

function TextArea_(
  props: TextAreaProps,
  ref?: ForwardedRef<HTMLTextAreaElement>
) {
  const {
    label,
    hideLabel = false,
    invalid,
    value: valueProp = '',
    className: classNameFromProps,
    in: inFromProps,
    state: stateFromProps,
    onChange,
    onBlur,
    onFocus,
    ...textAreaProps
  } = props;

  const [state, handlers] = useElementState({
    autoFocus: props.autoFocus,
    disabled: props.disabled,
    invalid,
  });

  const [value, setValue] = useState(valueProp);

  const handleFocus: FocusEventHandler<HTMLTextAreaElement> = e => {
    e.persist();
    handlers.onFocus(e);
    onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> = e => {
    e.persist();
    handlers.onBlur(e);
    onBlur?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = e => {
    e.persist();
    setValue(e.target.value);
    onChange?.(e);
  };

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  const fieldLabelIn =
    state === 'focus' || state === 'invalid' || value.length > 0;

  // Placeholder must be unset if the label is displayed in the field (e.g.
  // before focusing), otherwise they will visually overlap each others.
  // We use `aria-placeholder` to keep it unchanged in the accessibility tree.
  const placeholder =
    label && !fieldLabelIn ? undefined : textAreaProps.placeholder;

  return (
    <FieldLabel
      className={classNames(styles.fieldLabel, classNameFromProps)}
      hideLabel={hideLabel}
      label={label}
      in={fieldLabelIn}
      state={state}
    >
      <Text asChild>
        <textarea
          {...textAreaProps}
          ref={ref}
          value={value}
          placeholder={placeholder}
          /** Make sure the ARIA placeholder is kept in the a11y tree. */
          aria-placeholder={textAreaProps.placeholder}
          aria-invalid={invalid}
          className={styles.textarea}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </Text>
    </FieldLabel>
  );
}
